import { Button, Grid, Typography } from '@mui/material'
import { TextFieldWithLabel } from 'Components/TextField'
import { type ChildValidationState, type ChildShortFormState } from 'Pages/CaregiverPortal/types'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { type lookup } from 'core/types'
import { TextAreaWithLabel } from 'Components/TextArea'
import RemoveIcon from '@mui/icons-material/Remove'
import { CustomLabelWithToolTip } from 'Components/CustomLabel/Index'

interface ChildShortFormProps {
  index: number
  isUnborn: boolean
  pageState: ChildShortFormState
  validationState: ChildValidationState
  setValidationState: (newState: ChildValidationState) => void
  validateFormField: (value: any, name: string) => boolean
  relationshipToChildOptions: lookup[]
  languageList: lookup[]
  isdId?: string
  onUpdate: (name: string, value: any) => void
  onRemove: (index: number) => void
}

const genderOptions: Array<{ id: string, name: string }> = [
  { id: 'M', name: 'Male' },
  { id: 'F', name: 'Female' }
]

export function ChildShortForm (props: ChildShortFormProps): JSX.Element {
  const { pageState, validationState } = props

  const handleChange = (name: string, value: any): void => {
    props.onUpdate(name, value)
    validateFormField(name, value)
  }

  const handleBlur = (event: any): void => {
    const { name, value } = event.target
    validateFormField(name, value)
  }

  const validateFormField = (name: string, value: any): void => {
    const fieldName = name.substring(name.indexOf('_') + 1)
    props.setValidationState({ ...validationState, [fieldName]: { isDirty: true, isValid: props.validateFormField(value, name) } })
  }

  return <>
    {props.index > 0 && <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
      <Typography variant='h6'>Child {props.index + 1}</Typography>
      <Button variant='text' onClick={() => { props.onRemove(props.index) }}><RemoveIcon /> Remove</Button>
    </Grid>}
    <Grid item xs={12}>
      <TextFieldWithLabel
        label={'First Name'}
        name={`${props.index}_firstName`}
        maxLength={50}
        data-testid={`${props.index}_firstName`}
        onChange={(e) => { handleChange(e.target.name, e.target.value) }}
        onBlur={handleBlur}
        value={pageState.firstName}
        error={validationState.firstName.isDirty && !validationState.firstName.isValid}
        required={!props.isUnborn}
        showRequiredText={validationState.firstName.isDirty && !validationState.firstName.isValid}
      />
    </Grid>
    <Grid item xs={12}>
      <TextFieldWithLabel
        label={'Last Name'}
        name={`${props.index}_lastName`}
        maxLength={50}
        data-testid={`${props.index}_lastName`}
        onChange={(e) => { handleChange(e.target.name, e.target.value) }}
        // onBlur={handleBlur}
        value={pageState.lastName}
        error={validationState.lastName.isDirty && !validationState.lastName.isValid}
        required={true}
        showRequiredText={validationState.lastName.isDirty && !validationState.lastName.isValid}
      />
    </Grid>
    <Grid item xs={12}>
      <DatePickerWithLabel
        label={ props.isUnborn ? 'Due Date' : 'Date of Birth' }
        name={`${props.index}_dateOfBirth`}
        data-testid={`${props.index}_dateOfBirth`}
        FormControlProps={{ className: 'width-100' }}
        onChange={(e) => { handleChange(`${props.index}_dateOfBirth`, e) }}
        // onBlur={handleBlur}
        error={validationState.dateOfBirth.isDirty && !validationState.dateOfBirth.isValid}
        required={!props.isUnborn}
        showRequiredText={validationState.dateOfBirth.isDirty && !validationState.dateOfBirth.isValid}
        value={pageState.dateOfBirth}
      />
    </Grid>
    <Grid item xs={12}>
      <SelectWithLabel
        width='100%'
        name={`${props.index}_relationshipToChildId`}
        label='Your Relationship to Child'
        data-testid={`${props.index}_relationshipToChildId`}
        options={props.relationshipToChildOptions}
        onChange={(e) => { handleChange(e.target.name, e.target.value) }}
        value={pageState.relationshipToChildId ?? 0}
        error={validationState.relationshipToChildId.isDirty && !validationState.relationshipToChildId.isValid}
        required={true}
        showRequiredText={validationState.relationshipToChildId.isDirty && !validationState.relationshipToChildId.isValid}
      />
    </Grid>
    <Grid item xs={12}>
      <SelectWithLabel
        width='100%'
        name={`${props.index}_birthGender`}
        label='Assigned Gender at Birth'
        data-testid={`${props.index}_birthGender`}
        options={genderOptions}
        showRequiredText={true}
        required={false}
        onChange={(e) => { handleChange(e.target.name, e.target.value) }}
        value={pageState.birthGender ?? 0}
        labelContent={<CustomLabelWithToolTip
          name='IF_Child_Gender'
          isdId={props.isdId ?? 0}
          defaultLabelText='Assigned Gender at Birth'
          defaultTooltipText={'This gender must match the gender on your child\'s birth certificate'}
        />}
      />
    </Grid>
    <Grid item xs={12}>
      <SelectWithLabel
        width='100%'
        name={`${props.index}_primaryLanguageId`}
        label='Primary Language (if not English)'
        data-testid={`${props.index}_primaryLanguageId`}
        options={props.languageList}
        onChange={(e) => { handleChange(e.target.name, e.target.value) }}
        value={pageState.primaryLanguageId ?? ''}
        showRequiredText={true}
        required={false}
      />
    </Grid>
    <Grid item xs={12}>
      <TextAreaWithLabel
        label={'Is there anything you would like us to know about your child?'}
        name={`${props.index}_notes`}
        data-testid={`${props.index}_notes`}
        onChange={(e) => { handleChange(e.target.name, e.target.value) }}
        value={pageState.notes}
        multiline={true}
      />
    </Grid>
  </>
}
